import { PanoMeasureToolRenderer } from "@/components/r3f/renderers/measurements/pano-measure-tool-renderer";
import { isAddWallState } from "@/components/ui/pano-measurement-tool-panel";
import { selectIsPanoWithDepth } from "@/modes/walk-mode/walk-scene-selectors";
import { PanoObject } from "@/object-cache";
import {
  selectCameraHeight,
  selectPanoMeasurementToolState,
} from "@/store/measurement-tool-selector";
import {
  addPanoMeasurementWall,
  CAMERA_HEIGHT_DEFAULT,
  PanoMeasurementToolState,
  PanoMeasurementWall,
  setCameraPosition,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { deactivateTool } from "@/store/ui/ui-slice";
import {
  computeNdcCoordinates,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import { generateGUID } from "@faro-lotv/foundation";
import { planeLineIntersect } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { MOUSE, Plane, Ray, Raycaster, Vector3 } from "three";
import { useLensCursor } from "./use-lens-cursor";

export type PanoMesurementToolsProps = {
  /** The pano object */
  pano: PanoObject | undefined;

  /** Set enable/disable state of the measurement controls */
  setEnableControls(enable: boolean): void;
};

/**
 * @returns Measurement tool for pano without depth data
 */
export function PanoMeasurementTool({
  pano,
  setEnableControls,
}: PanoMesurementToolsProps): JSX.Element | null {
  const currentState = useAppSelector(selectPanoMeasurementToolState);
  const cameraHeight = useAppSelector(selectCameraHeight(pano?.iElement.id));
  const dispatch = useAppDispatch();

  const isPanoWithDepth = useAppSelector(selectIsPanoWithDepth(pano?.iElement));

  useEffect(() => {
    // update measurement tool state based on pano and its depth data
    if (pano === undefined || isPanoWithDepth) {
      dispatch(setPanoMeasurementToolState(PanoMeasurementToolState.None));
      return;
    }
    if (currentState === PanoMeasurementToolState.StartMeasure) {
      dispatch(
        setPanoMeasurementToolState(
          cameraHeight === undefined
            ? PanoMeasurementToolState.SetCameraHeight
            : PanoMeasurementToolState.SelectFloorPoint1,
        ),
      );
    }
    // only enable measurement controls when in measuring state
    setEnableControls(currentState === PanoMeasurementToolState.Measuring);
  }, [
    currentState,
    dispatch,
    cameraHeight,
    isPanoWithDepth,
    pano,
    setEnableControls,
  ]);

  const eventTarget = useThreeEventTarget();
  const [raycaster] = useState(() => new Raycaster());
  const { camera } = useThree();

  useEffect(() => {
    if (pano) {
      dispatch(
        setCameraPosition({
          panoId: pano.iElement.id,
          position: camera.position.toArray(),
        }),
      );
    }
  }, [camera.position, dispatch, pano]);

  const [floorPoint1, setFloorPoint1] = useState<Vector3>();
  const [floorPoint2, setFloorPoint2] = useState<Vector3>();
  const [ceilingPoint, setCeilingPoint] = useState<Vector3>();
  const getCameraRay = useCallback(
    (event: PointerEvent): Ray => {
      const mouse = computeNdcCoordinates(
        event.clientX,
        event.clientY,
        eventTarget,
      );
      raycaster.setFromCamera(mouse, camera);
      return raycaster.ray;
    },
    [camera, eventTarget, raycaster],
  );

  const pointerMove = useCallback(
    (event: PointerEvent): void => {
      switch (currentState) {
        case PanoMeasurementToolState.SelectFloorPoint1:
          {
            event.stopPropagation();
            const p1 = getRayFloorIntersection(
              getCameraRay(event),
              cameraHeight ?? CAMERA_HEIGHT_DEFAULT,
            );
            setFloorPoint1(p1);
            setFloorPoint2(undefined);
            setCeilingPoint(undefined);
          }
          break;
        case PanoMeasurementToolState.SelectFloorPoint2:
          event.stopPropagation();
          if (floorPoint1 !== undefined) {
            const p2 = getRayFloorIntersection(
              getCameraRay(event),
              cameraHeight ?? CAMERA_HEIGHT_DEFAULT,
            );
            setFloorPoint2(p2);
            setCeilingPoint(undefined);
          }
          break;
        case PanoMeasurementToolState.SelectCeilingPoint:
          event.stopPropagation();
          if (floorPoint1 !== undefined && floorPoint2 !== undefined) {
            const pt = getRayWallIntersection(
              getCameraRay(event),
              floorPoint1,
              floorPoint2,
            );
            setCeilingPoint(pt);
          }
          break;
      }
    },
    [cameraHeight, currentState, getCameraRay, floorPoint1, floorPoint2],
  );

  const pointerX = useRef(0);
  const pointerY = useRef(0);
  const pointerDown = useCallback((event: PointerEvent): void => {
    if (event.button !== MOUSE.LEFT) return;
    pointerX.current = event.clientX;
    pointerY.current = event.clientY;
  }, []);

  const pointerUp = useCallback(
    (event: PointerEvent): void => {
      if (event.button !== MOUSE.LEFT) return;
      const deltaX = Math.abs(event.clientX - pointerX.current);
      const deltaY = Math.abs(event.clientY - pointerY.current);
      if (deltaX > 2 || deltaY > 2) return;

      if (pano === undefined) return;
      switch (currentState) {
        case PanoMeasurementToolState.SelectFloorPoint1:
          event.stopPropagation();
          if (floorPoint1 !== undefined) {
            dispatch(
              setPanoMeasurementToolState(
                PanoMeasurementToolState.SelectFloorPoint2,
              ),
            );
          }
          break;
        case PanoMeasurementToolState.SelectFloorPoint2:
          event.stopPropagation();
          if (floorPoint1 !== undefined && floorPoint2 !== undefined) {
            dispatch(
              setPanoMeasurementToolState(
                PanoMeasurementToolState.SelectCeilingPoint,
              ),
            );
          }
          break;
        case PanoMeasurementToolState.SelectCeilingPoint:
          event.stopPropagation();
          if (
            floorPoint1 !== undefined &&
            floorPoint2 !== undefined &&
            ceilingPoint !== undefined
          ) {
            const wall: PanoMeasurementWall = {
              id: generateGUID(),
              panoId: pano.iElement.id,
              floorPoint1: floorPoint1.toArray(),
              floorPoint2: floorPoint2.toArray(),
              height: ceilingPoint.y - floorPoint1.y,
            };
            dispatch(
              addPanoMeasurementWall({
                panoId: pano.iElement.id,
                wall,
              }),
            );
            setFloorPoint1(undefined);
            setFloorPoint2(undefined);
            setCeilingPoint(undefined);
            dispatch(
              setPanoMeasurementToolState(PanoMeasurementToolState.Measuring),
            );
          }
          break;
      }
    },
    [pano, currentState, floorPoint1, floorPoint2, ceilingPoint, dispatch],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        switch (currentState) {
          case PanoMeasurementToolState.SelectCeilingPoint:
            setCeilingPoint(undefined);
            setFloorPoint2(undefined);
            dispatch(
              setPanoMeasurementToolState(
                PanoMeasurementToolState.SelectFloorPoint2,
              ),
            );
            break;
          case PanoMeasurementToolState.SelectFloorPoint2:
            setFloorPoint2(undefined);
            setFloorPoint1(undefined);
            dispatch(
              setPanoMeasurementToolState(
                PanoMeasurementToolState.SelectFloorPoint1,
              ),
            );
            break;
          default:
            dispatch(deactivateTool());
            dispatch(
              setPanoMeasurementToolState(PanoMeasurementToolState.None),
            );
        }
      }
    },
    [currentState, dispatch],
  );

  useLensCursor(isAddWallState(currentState));

  useEffect(() => {
    eventTarget.addEventListener("pointermove", pointerMove);
    eventTarget.addEventListener("pointerdown", pointerDown);
    eventTarget.addEventListener("pointerup", pointerUp);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      eventTarget.removeEventListener("pointermove", pointerMove);
      eventTarget.removeEventListener("pointerdown", pointerDown);
      eventTarget.removeEventListener("pointerup", pointerUp);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [eventTarget, handleKeyDown, pointerDown, pointerMove, pointerUp]);

  // On component unmount, we ensure that the 'enableControls' state is reset to true,
  // so that the measures on 3D models are enabled when this wizard for panos without depth
  // is not needed anymore.
  useEffect(() => () => setEnableControls(true), [setEnableControls]);

  if (pano === undefined || isPanoWithDepth) {
    return null;
  }

  return (
    <PanoMeasureToolRenderer
      state={currentState}
      floorPoint1={floorPoint1}
      floorPoint2={floorPoint2}
      ceilingPoint={ceilingPoint}
    />
  );
}

function getRayFloorIntersection(
  ray: Ray,
  cameraHeight: number,
): Vector3 | undefined {
  // intersect ray with horizontal plane at camera.y - cameraHeight
  const t = -cameraHeight / ray.direction.y;
  if (t < 0) return;

  const pt = ray.origin.clone();
  pt.addScaledVector(ray.direction, t);
  return pt;
}

// cached temporary objects for avoiding repleated allocation
const tmpObjs = {
  yAxis: new Vector3(0, 1, 0),
  normal: new Vector3(),
  plane: new Plane(),
};

function getRayWallIntersection(
  ray: Ray,
  point1: Vector3,
  point2: Vector3,
): Vector3 | undefined {
  tmpObjs.normal.subVectors(point2, point1);
  tmpObjs.normal.cross(tmpObjs.yAxis);
  tmpObjs.plane.setFromNormalAndCoplanarPoint(tmpObjs.normal, point1);

  const pt = planeLineIntersect(tmpObjs.plane, {
    point: ray.origin,
    direction: ray.direction,
  });
  return pt;
}
